import React, { useState, useEffect, useRef } from 'react';
import { Box, Tabs, Tab, Paper, useTheme, useMediaQuery } from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import Charts from '../Charts/Charts';
import OptionChainOverview from './OptionChainOverview';
import { fetchScripInfo } from '../../services/backendAPI';
import { shouldEnableOptionChain } from '../../services/utilities';

const Overview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { exchange, token, price_precision } = useParams();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(0);
  const [scripInfo, setScripInfo] = useState(null);
  const [showOptionChain, setShowOptionChain] = useState(false);
  
  // Track route changes to reset state synchronously
  const currentRouteKey = `${exchange}-${token}`;
  const [prevRouteKey, setPrevRouteKey] = useState(currentRouteKey);

  // Derived state: Immediately reset state on navigation to avoid mounting 
  // child components (like OptionChainOverview) with stale/mismatched data
  if (currentRouteKey !== prevRouteKey) {
    setPrevRouteKey(currentRouteKey);
    setScripInfo(null);
    setShowOptionChain(false);
    
    if (location.state?.defaultTab === 'chart') {
      setActiveTab(0);
    } else {
      setActiveTab(0); // Safest to default to 0 until new scripInfo arrives and verifies option chain availability
    }
  }
  
  // Refs for position monitoring
  const tabsRef = useRef(null);
  const contentRef = useRef(null);
  const scrollDetectedRef = useRef(false);

  // iPhone detection
  const isIPhone = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSafari = /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);

  // Check if we have required parameters
  const hasRequiredParams = !!(exchange && token);

  console.log('Overview component loaded:', {
    isIPhone,
    isSafari,
    userAgent: navigator.userAgent,
    pathname: location.pathname,
    exchange,
    token,
    hasRequiredParams
  });

  // Function to check if iframe is positioned correctly
  const checkIframePosition = () => {
    if (!isIPhone || !tabsRef.current || !contentRef.current) return true;
    
    try {
      const tabsRect = tabsRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();
      const iframe = contentRef.current.querySelector('iframe');
      
      if (iframe) {
        const iframeRect = iframe.getBoundingClientRect();
        
        // Check if iframe is overlapping with tabs (wrong position)
        const isOverlapping = iframeRect.top < tabsRect.bottom;
        
        console.log('Position check:', {
          tabsBottom: tabsRect.bottom,
          contentTop: contentRect.top,
          iframeTop: iframeRect.top,
          isOverlapping: isOverlapping,
          scrollY: window.scrollY
        });
        
        return !isOverlapping;
      }
    } catch (error) {
      console.log('Position check error:', error);
    }
    
    return true;
  };

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  
  // Scroll handler for iPhone - Only when we have proper parameters
  useEffect(() => {
    if (!isIPhone || !hasRequiredParams) return;
    
    let scrollTimeout;
    let positionCheckInterval;
    
    const handleScroll = () => {
      console.log('Scroll detected on iPhone');
      scrollDetectedRef.current = true;
      
      // Clear existing timeout
      clearTimeout(scrollTimeout);
      
      // Check position after scroll stops
      scrollTimeout = setTimeout(() => {
        const routeKey = `${exchange}-${token}`;
        const positionKey = `iphone-position-check-${routeKey}`;
        
        if (!checkIframePosition()) {          
          // Store that we detected position corruption
          localStorage.setItem(positionKey, JSON.stringify({
            timestamp: Date.now(),
            scrollY: window.scrollY,
            reason: 'position-corruption'
          }));
          
          // Clear reload keys to ensure reload happens
          Object.keys(sessionStorage).forEach(key => {
            if (key.includes('overview') && key.includes('reload')) {
              sessionStorage.removeItem(key);
            }
          });
          
          // Reload page
          window.location.reload(true);
        }
      }, 500); // Wait 500ms after scroll stops
    };
    
    // Monitor position periodically after scroll detection
    const startPositionMonitoring = () => {
      if (scrollDetectedRef.current && !positionCheckInterval) {
        positionCheckInterval = setInterval(() => {
          if (!checkIframePosition()) {
            clearInterval(positionCheckInterval);
            handleScroll(); // Trigger reload logic
          }
        }, 1000); // Check every second
      }
    };
    
    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('touchmove', handleScroll, { passive: true });
    
    // Start monitoring after component is loaded
    setTimeout(startPositionMonitoring, 2000);
    
    return () => {
      clearTimeout(scrollTimeout);
      clearInterval(positionCheckInterval);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('touchmove', handleScroll);
    };
  }, [isIPhone, hasRequiredParams, exchange, token]);

  // Check if we reloaded due to position corruption - Only when we have proper parameters
  useEffect(() => {
    if (!isIPhone || !hasRequiredParams) return;
    
    const routeKey = `${exchange}-${token}`;
    const positionKey = `iphone-position-check-${routeKey}`;
    const positionData = localStorage.getItem(positionKey);
    
    if (positionData) {
      try {
        const data = JSON.parse(positionData);
        console.log('Previous position corruption detected:', data);
        
        // Clear the flag after checking
        localStorage.removeItem(positionKey);
        
        // Wait a bit then check if position is now correct
        setTimeout(() => {
          if (checkIframePosition()) {
            console.log('✅ Position fixed after reload');
          } else {
            console.log('❌ Position still corrupted after reload');
          }
        }, 1000);
      } catch (error) {
        console.log('Error parsing position data:', error);
      }
    }
  }, [isIPhone, hasRequiredParams, exchange, token]);

  // Immediate iPhone reload check - Only when we have proper parameters
  useEffect(() => {
    if (!isIPhone && !isSafari) return;
    if (!hasRequiredParams) return; // Critical: Only reload for valid routes
    
    const routeKey = `${exchange}-${token}`;
    const reloadKey = `overview-immediate-reload-${routeKey}`;
    const hasReloaded = sessionStorage.getItem(reloadKey);
    
    console.log('Immediate reload check:', {
      routeKey,
      reloadKey,
      hasReloaded,
      exchange,
      token
    });
    
    if (!hasReloaded) {
      console.log('🔄 IMMEDIATE iPhone/Safari reload triggered for valid route');
      sessionStorage.setItem(reloadKey, Date.now().toString());
      
      // Clear any existing reload keys to ensure fresh start
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('iphone-overview-reload-') || key.startsWith('overview-reload-')) {
          sessionStorage.removeItem(key);
        }
      });
      
      // Force immediate reload
      window.location.reload(true);
      return;
    }
  }, [hasRequiredParams, exchange, token, isIPhone, isSafari]);

  // iPhone auto-reload when routing to Overview component - Only for valid routes
  useEffect(() => {
    console.log('iPhone reload effect triggered:', { 
      isIPhone, 
      isSafari, 
      pathname: location.pathname,
      exchange,
      token,
      hasRequiredParams
    });
    
    if (!isIPhone && !isSafari) return;
    if (!hasRequiredParams) {
      console.log('⚠️ Skipping iPhone reload - missing required parameters');
      return; // Critical: Don't reload for invalid routes
    }
    
    // Create a unique key for this specific overview route
    const currentPath = location.pathname;
    const sessionKey = `iphone-overview-reload-${currentPath}`;
    const hasReloadedForThisRoute = sessionStorage.getItem(sessionKey);
    
    console.log('Checking reload status:', {
      currentPath,
      sessionKey,
      hasReloadedForThisRoute,
      sessionStorage: sessionStorage.getItem(sessionKey)
    });
    
    if (!hasReloadedForThisRoute) {
      console.log('🔄 iPhone/Safari detected - RELOADING to fix iframe layout for route:', currentPath);
      
      // Mark as reloaded BEFORE reloading
      sessionStorage.setItem(sessionKey, Date.now().toString());
      
      // Force immediate reload with cache bypass
      window.location.reload(true);
      return; // Exit early to prevent further execution
    } else {
      console.log('✅ iPhone/Safari Overview - already reloaded for this route:', currentPath);
    }
  }, [location.pathname, isIPhone, isSafari, hasRequiredParams, exchange, token]);

  // Alternative reload method - Only for valid routes
  useEffect(() => {
    if (!isIPhone && !isSafari) return;
    if (!hasRequiredParams) return; // Critical: Only reload for valid routes
    
    const routeKey = `${exchange}-${token}`;
    const reloadKey = `overview-reload-${routeKey}`;
    const hasReloaded = localStorage.getItem(reloadKey);
    
    console.log('Alternative reload check:', {
      routeKey,
      reloadKey,
      hasReloaded,
      exchange,
      token
    });
    
    if (!hasReloaded) {
      console.log('🔄 Alternative reload triggered for iPhone/Safari');
      localStorage.setItem(reloadKey, 'true');
      
      // Use location.replace for immediate reload
      setTimeout(() => {
        window.location.replace(window.location.href);
      }, 50);
      return;
    }
  }, [hasRequiredParams, exchange, token, isIPhone, isSafari]);

  // Fetch scrip info and check for options/futures availability
  useEffect(() => {
    // Don't fetch data if we're about to reload or missing params
    if (!hasRequiredParams) return;
    
    if (isIPhone || isSafari) {
      const currentPath = location.pathname;
      const sessionKey = `iphone-overview-reload-${currentPath}`;
      const hasReloadedForThisRoute = sessionStorage.getItem(sessionKey);
      
      if (!hasReloadedForThisRoute) {
        console.log('Skipping data fetch - reload pending');
        return; // Skip data fetching if reload is pending
      }
    }

    const getScripInfo = async () => {
      try {
        console.log('Fetching scrip info for:', exchange, token);
        const response = await fetchScripInfo(exchange, token);
        if (response.result) {
          setScripInfo(response.result);
          const optionChainEnabled = shouldEnableOptionChain(response.result);
          setShowOptionChain(optionChainEnabled);
        }
      } catch (error) {
        console.error('Error fetching scrip info:', error);
        setShowOptionChain(false);
      }
    };

    getScripInfo();
  }, [hasRequiredParams, exchange, token, location.pathname, isIPhone, isSafari]);

  // Set initial tab based on route state and option chain availability
  useEffect(() => {
    if (location.state?.defaultTab === 'chart') {
      setActiveTab(0);
    } else if (location.state?.defaultTab === 'optionchain' && showOptionChain) {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [location.state, location.pathname, showOptionChain]);

  // NOW WE CAN SAFELY DO EARLY RETURN AFTER ALL HOOKS

  // Early return if we don't have required parameters
  if (!hasRequiredParams) {
    console.log('⚠️ Overview accessed without required parameters - showing message');
    return (
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 0
        }}
      >
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Box sx={{ color: 'text.secondary', mb: 2 }}>
            Invalid Overview Route
          </Box>
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            Please select a symbol from the market watch to view charts and option chain.
          </Box>
        </Box>
      </Paper>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        overflow: 'hidden',
        borderRadius: 0,
        position: 'relative'
      }}
    >
      {/* Fixed Tabs Row */}
      <Box 
        ref={tabsRef}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: 10,
          position: 'relative'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: 40,
            '& .MuiTabs-indicator': {
              height: 2
            }
          }}
        >
          <Tab
            label="Charts"
            sx={{
              textTransform: 'none',
              minHeight: 40,
              px: 3
            }}
          />
          {showOptionChain && (
            <Tab
              label="Option Chain"
              sx={{
                textTransform: 'none',
                minHeight: 40,
                px: 3
              }}
            />
          )}
        </Tabs>
      </Box>

      {/* Content Row - Takes remaining space */}
      <Box 
        ref={contentRef}
        sx={{
          overflow: 'hidden',
          position: 'relative',
          height: '100%',
          minHeight: 0, // Important for grid child
          contain: 'layout style paint',
          isolation: 'isolate'
        }}
      >
        {activeTab === 0 ? (
          <Box sx={{ 
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            overflow: 'hidden',
            contain: 'strict',
            isolation: 'isolate'
          }}>
            <Charts
              exchange={exchange}
              token={token}
              theme={theme}
              pricePrecision={price_precision}
              scripInfo={scripInfo}
            />
          </Box>
        ) : showOptionChain && (
          <Box sx={{ 
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            overflow: 'auto',
            contain: 'layout style paint'
          }}>
            <OptionChainOverview
              exchange={exchange}
              token={token}
              scripInfo={scripInfo}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default Overview; 